import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconEye,
  IconCancel,
  IconCircleCheck,
  IconDownload,
  IconRefresh,
  IconLink,
} from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';

export interface TeeActionsMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  destructive?: boolean;
  disabled?: boolean;
}

export interface TeeActionsMenuProps {
  /** When provided, only these items are rendered (fixed slot items are ignored). */
  items?: TeeActionsMenuItem[];
  triggerAriaLabel?: string;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  viewLabel?: string;
  editLabel?: string;
  deleteLabel?: string;
  className?: string;
  onToggleStatus?: () => void;
  status?: 'active' | 'inactive' | 'skipped';
  onCheckIn?: () => void;
  onCancel?: () => void;
  checkInLabel?: string;
  checkInDisabled?: boolean;
  checkInDisabledLabel?: string;
  cancelLabel?: string;
  onDownload?: () => void;
  downloadLabel?: string;
  onRetry?: () => void;
  retryLabel?: string;
  onSyncCrm?: () => void;
  syncCrmLabel?: string;
  syncCrmDisabled?: boolean;
  onCopyDeepLink?: () => void;
  copyDeepLinkLabel?: string;
}

export const TeeActionsMenu = React.forwardRef<
  HTMLDivElement,
  TeeActionsMenuProps
>(
  (
    {
      items,
      triggerAriaLabel,
      onView,
      onEdit,
      onDelete,
      viewLabel,
      editLabel,
      deleteLabel,
      className,
      onToggleStatus,
      status,
      onCheckIn,
      onCancel,
      checkInLabel,
      checkInDisabled,
      checkInDisabledLabel,
      cancelLabel,
      onDownload,
      downloadLabel,
      onRetry,
      retryLabel,
      onSyncCrm,
      syncCrmLabel,
      syncCrmDisabled,
      onCopyDeepLink,
      copyDeepLinkLabel,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    const handleButtonClick = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom,
          left: rect.right - 120,
        });
      }
      setIsOpen(!isOpen);
    };

    const handleView = () => {
      onView?.();
      setIsOpen(false);
    };

    const handleRetry = () => {
      onRetry?.();
      setIsOpen(false);
    };

    const handleSyncCrm = () => {
      if (syncCrmDisabled) return;
      onSyncCrm?.();
      setIsOpen(false);
    };

    const handleEdit = () => {
      onEdit?.();
      setIsOpen(false);
    };

    const handleDelete = () => {
      onDelete?.();
      setIsOpen(false);
    };

    const handleToggleStatus = () => {
      onToggleStatus?.();
      setIsOpen(false);
    };

    const handleCheckIn = () => {
      onCheckIn?.();
      setIsOpen(false);
    };

    const handleCancel = () => {
      onCancel?.();
      setIsOpen(false);
    };

    const handleDownload = () => {
      onDownload?.();
      setIsOpen(false);
    };

    const handleCopyDeepLink = () => {
      onCopyDeepLink?.();
      setIsOpen(false);
    };

    return (
      <div ref={ref} className={cn('relative', className)}>
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className="p-1 rounded-md hover:bg-[var(--background-card-hover)] transition-colors"
          title="Actions"
          aria-label={triggerAriaLabel ?? 'Actions menu'}
        >
          <IconDotsVertical
            size={20}
            className="text-[var(--text-content-sub-title)]"
          />
        </button>

        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              className="fixed w-auto bg-[var(--background-card)] rounded-md shadow-[0px_8px_32px_0px_rgba(0,0,0,0.5)] border border-[var(--border-default)] z-50 py-1.5"
              style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              {items &&
                items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    aria-disabled={item.disabled || undefined}
                    onClick={() => {
                      if (item.disabled) return;
                      item.onClick();
                      setIsOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2 text-body-xs transition-colors',
                      item.destructive
                        ? 'text-[var(--color-maroon)] hover:bg-[var(--background-badge-red)]'
                        : 'text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)]',
                      item.disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}

              {!items && onView && (
                <button
                  onClick={handleView}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors first:rounded-t-md"
                >
                  <IconEye size={16} />
                  <span>{viewLabel || t('common.view')}</span>
                </button>
              )}

              {!items && onRetry && (
                <button
                  onClick={handleRetry}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors"
                >
                  <IconRefresh size={16} />
                  <span>{retryLabel || t('common.retry')}</span>
                </button>
              )}

              {!items && onSyncCrm && (
                syncCrmDisabled ? (
                  <button
                    type="button"
                    disabled
                    className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] opacity-50 cursor-not-allowed"
                  >
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeDasharray="15.7 62.8"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>{syncCrmLabel || t('members.syncFromCrm')}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSyncCrm}
                    className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors"
                  >
                    <IconRefresh size={16} />
                    <span>{syncCrmLabel || t('members.syncFromCrm')}</span>
                  </button>
                )
              )}

              {!items && onEdit && (
                <button
                  onClick={handleEdit}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors',
                    !onView && 'first:rounded-t-md'
                  )}
                >
                  <IconEdit size={16} />
                  <span>{editLabel || t('common.edit')}</span>
                </button>
              )}

              {!items && onCheckIn && (
                checkInDisabled ? (
                  <button
                    disabled
                    className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] opacity-50 cursor-not-allowed"
                    title={checkInDisabledLabel}
                  >
                    <IconCircleCheck size={16} />
                    <span>{checkInLabel || t('common.checkIn')}</span>
                  </button>
                ) : (
                  <button
                    onClick={handleCheckIn}
                    className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors"
                  >
                    <IconCircleCheck size={16} />
                    <span>{checkInLabel || t('common.checkIn')}</span>
                  </button>
                )
              )}

              {!items && onDownload && (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors first:rounded-t-md last:rounded-b-md"
                >
                  <IconDownload size={16} />
                  <span>{downloadLabel ?? t('common.actions.downloadPdf')}</span>
                </button>
              )}

              {!items && onCopyDeepLink && (
                <button
                  type="button"
                  onClick={handleCopyDeepLink}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors first:rounded-t-md last:rounded-b-md"
                >
                  <IconLink size={16} />
                  <span>{copyDeepLinkLabel ?? t('common.actions.copyDeepLink')}</span>
                </button>
              )}

              {!items && onToggleStatus && status !== 'skipped' && (
                <button
                  onClick={handleToggleStatus}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-[hsl(var(--foreground))] hover:bg-[var(--background-card-elevated)] transition-colors last:rounded-b-md"
                >
                  {status === 'active' ? (
                    <IconCancel size={16} />
                  ) : (
                    <IconCircleCheck size={16} />
                  )}
                  <span>
                    {status === 'active'
                      ? t('common.deactivate')
                      : t('common.activate')}
                  </span>
                </button>
              )}

              {!items && onCancel && (
                <button
                  onClick={handleCancel}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-red-500 hover:bg-[rgba(239,68,68,0.1)] transition-colors"
                >
                  <IconCancel size={16} />
                  <span>{cancelLabel || t('common.cancel')}</span>
                </button>
              )}

              {!items && onDelete && (
                <button
                  onClick={handleDelete}
                  className="w-full flex items-center gap-3 px-4 py-2 text-body-xs text-red-500 hover:bg-[rgba(239,68,68,0.1)] transition-colors last:rounded-b-md"
                >
                  <IconTrash size={16} />
                  <span>{deleteLabel || t('common.delete')}</span>
                </button>
              )}
            </div>,
            document.body
          )}
      </div>
    );
  }
);

TeeActionsMenu.displayName = 'TeeActionsMenu';

export default TeeActionsMenu;
